import { useEffect, useMemo, useState } from "react";
import type { DeskSession } from "../hooks/useDeskSession";
import { nextOpId } from "../lib/edit-helpers";
import { focusBeatId } from "../lib/focus-frame.js";
import { ToolCartButton } from "./ToolCartButton.js";
import {
  FOCUS_ZOOM_MAX_PCT,
  FOCUS_ZOOM_MIN_PCT,
  FOCUS_ZOOM_STEP_PCT,
  focusCropForZoom,
  focusZoomPctFromFrame,
} from "../lib/tool-selection.js";

/**
 * Focus is now a single ZOOM control. 1.0x is Wide (full frame, no crop); higher
 * zoom shrinks the crop around its centre and reveals drag-to-pan on the video
 * (handled in EditorScreen). No presets, no subject buttons. The crop rect it
 * writes is the same one the renderer and the "How it's landing" vector consume,
 * so a tighter zoom keeps pushing Pity/Doubt.
 */
export function FrameTool({ session }: { session: DeskSession }) {
  const { pack, packId, resolved, applyOp, timeline, tick } = session;

  const beatId = useMemo(
    () => focusBeatId(resolved, timeline, tick, pack),
    [resolved, timeline, tick, pack],
  );

  const frame = resolved.frames.get(beatId);

  const [zoomPct, setZoomPct] = useState(() => focusZoomPctFromFrame(frame));

  // Re-sync the slider when the acting beat / pack / committed frame changes
  // (undo, redo, playhead move, pan commit that changed the frame op).
  useEffect(() => {
    setZoomPct(focusZoomPctFromFrame(frame));
  }, [packId, beatId, frame]);

  const applyZoom = (pct: number) => {
    setZoomPct(pct);
    const prev = resolved.frames.get(beatId)?.crop ?? null;
    const crop = focusCropForZoom(pct, prev);
    applyOp({
      kind: "frame",
      opId: nextOpId("frame"),
      beatId,
      subjectEntityId: null,
      crop,
      zoomMilli: 1000,
    });
  };

  const zoomLabel = (zoomPct / 100).toFixed(1);

  return (
    <div className="tool-panel kid-tool" data-testid="frame-tool">
      <ToolCartButton tool="frame" />
      <h3>Focus</h3>

      <div className="focus-zoom" data-testid="focus-zoom">
        <div className="focus-zoom-head">
          <span className="focus-zoom-label">Zoom</span>
          <span className="focus-zoom-value" data-testid="focus-zoom-value">
            {zoomLabel}x{zoomPct <= FOCUS_ZOOM_MIN_PCT ? " (Wide)" : ""}
          </span>
        </div>
        <div className="focus-zoom-row">
          <button
            type="button"
            className="focus-zoom-step"
            data-testid="focus-zoom-out"
            aria-label="Zoom out"
            disabled={zoomPct <= FOCUS_ZOOM_MIN_PCT}
            onClick={() => applyZoom(Math.max(FOCUS_ZOOM_MIN_PCT, zoomPct - FOCUS_ZOOM_STEP_PCT))}
          >
            −
          </button>
          <input
            type="range"
            className="focus-zoom-slider"
            data-testid="focus-zoom-slider"
            min={FOCUS_ZOOM_MIN_PCT}
            max={FOCUS_ZOOM_MAX_PCT}
            step={FOCUS_ZOOM_STEP_PCT}
            value={zoomPct}
            aria-label="Zoom level"
            aria-valuetext={`${zoomLabel}x`}
            onChange={(e) => applyZoom(Number(e.target.value))}
          />
          <button
            type="button"
            className="focus-zoom-step"
            data-testid="focus-zoom-in"
            aria-label="Zoom in"
            disabled={zoomPct >= FOCUS_ZOOM_MAX_PCT}
            onClick={() => applyZoom(Math.min(FOCUS_ZOOM_MAX_PCT, zoomPct + FOCUS_ZOOM_STEP_PCT))}
          >
            +
          </button>
        </div>
      </div>
    </div>
  );
}
